/**
 * FlashingTemplatePickerInline — inline picker for choosing a Flashing
 * template on a single component row. Iter154 work, extracted Iter220
 * for reuse by the Repairs builders (Iter221+).
 *
 * Two modes — List default, Gallery alt. Mode persisted in localStorage.
 * 🔍 Preview popover works in both modes.
 *
 * Props:
 *   ref_id          — F-chip label (e.g. "F11"). Used for testid + popover id.
 *   eligible        — Pre-filtered list of flashing templates.
 *   picked          — Currently picked template object (or null).
 *   cat             — Flashing-Type category label (Apron, Back Tray, …).
 *   roofProfileCtx  — Roof profile hint shown in empty-state copy.
 *   onPick(tpl)     — Callback when operator picks a template (or null to clear).
 */
import React, { useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import FlashingProfileSVG from '@/components/FlashingProfileSVG';

export default function FlashingTemplatePickerInline({ ref_id, eligible, picked, cat, roofProfileCtx, onPick }) {
  const [mode, setMode] = useState(() => {
    if (typeof window === 'undefined') return 'list';
    return window.localStorage.getItem('rtr.flashingPickerMode') || 'list';
  });
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const setModeAndPersist = (next) => {
    setMode(next);
    try { window.localStorage.setItem('rtr.flashingPickerMode', next); } catch (e) { /* ignore */ }
    if (next === 'list') setGalleryOpen(false);
  };

  return (
    <div className="relative inline-flex items-start flex-wrap gap-1" data-testid={`flashing-template-picker-${ref_id}`}>
      <div className="inline-flex rounded border border-emerald-300 overflow-hidden mt-0.5" role="group" aria-label="Template picker mode">
        <button
          type="button"
          data-testid={`flashing-picker-mode-list-${ref_id}`}
          onClick={() => setModeAndPersist('list')}
          title="List mode — pick by name"
          className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${mode === 'list' ? 'bg-emerald-600 text-white' : 'bg-white text-emerald-700 hover:bg-emerald-50'}`}
        >List</button>
        <button
          type="button"
          data-testid={`flashing-picker-mode-gallery-${ref_id}`}
          onClick={() => { setModeAndPersist('gallery'); setGalleryOpen(true); }}
          title="Gallery mode — pick by drawing"
          className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border-l border-emerald-300 ${mode === 'gallery' ? 'bg-emerald-600 text-white' : 'bg-white text-emerald-700 hover:bg-emerald-50'}`}
        >Gallery</button>
      </div>

      {mode === 'list' && (
        <select
          data-testid={`comp-line-flashing-template-${ref_id}`}
          value={picked ? String(picked._id) : ''}
          onChange={(e) => {
            const id = e.target.value;
            const tpl = eligible.find(t => String(t._id) === id);
            onPick(tpl || null);
          }}
          title="Pick a flashing template. Templates are narrowed by category + your roof profile. Custom templates are always available."
          className="text-[11px] px-1.5 py-0.5 border border-emerald-200 rounded bg-white text-slate-700 min-w-[180px] mt-0.5"
        >
          <option value="">— Select template —</option>
          {eligible.length === 0 && <option disabled value="__none">No {cat} templates for {roofProfileCtx || 'this roof'}</option>}
          {eligible.map(t => (
            <option key={t._id} value={t._id}>
              {/* Iter246 — F count = bends (segments - 1). A flashing
                  with 3 length segments has 2 bends, hence 2F. Naming
                  matches user's mental model and the renamed rate
                  catalogue. */}
              {t.name} ({Math.max(0, (t.folds || []).length - 1)}F · {(t.folds || []).reduce((a, f) => a + (Number(f.length_mm) || 0), 0)}mm)
            </option>
          ))}
        </select>
      )}

      {mode === 'gallery' && (
        <>
          <button
            type="button"
            data-testid={`comp-line-flashing-template-${ref_id}`}
            onClick={() => setGalleryOpen(o => !o)}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 border border-emerald-200 rounded bg-white text-slate-700 min-w-[180px] hover:bg-emerald-50 mt-0.5"
          >
            <span className="truncate">{picked ? picked.name : `— Browse ${eligible.length} ${cat} templates —`}</span>
            <ChevronDown size={12} className="ml-auto text-emerald-600" />
          </button>
          {galleryOpen && (
            <div data-testid={`flashing-template-gallery-${ref_id}`} className="absolute z-30 top-full left-0 mt-1 bg-white border border-emerald-300 rounded-lg shadow-xl p-2 max-w-[560px] max-h-[380px] overflow-auto">
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">{cat} templates · {roofProfileCtx || 'all profiles'}</span>
                <button type="button" onClick={() => setGalleryOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={12} /></button>
              </div>
              {eligible.length === 0 ? (
                <div className="px-2 py-4 text-[11px] text-slate-500 italic">No {cat} templates available for {roofProfileCtx || 'this roof'}.</div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {eligible.map(t => {
                    const isPicked = picked && String(picked._id) === String(t._id);
                    const g = (t.folds || []).reduce((a, f) => a + (Number(f.length_mm) || 0), 0);
                    return (
                      <button
                        key={t._id}
                        type="button"
                        data-testid={`flashing-template-thumb-${t._id}`}
                        onClick={() => { onPick(t); setGalleryOpen(false); }}
                        title={`Pick ${t.name} (${Math.max(0, (t.folds || []).length - 1)} folds · ${g}mm girth)`}
                        className={`text-left rounded border-2 p-1 transition-all ${isPicked ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200' : 'border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50'}`}
                      >
                        <FlashingProfileSVG
                          folds={t.folds || []}
                          startAngle={Number(t.start_angle_deg) || 0}
                          width={160} height={80}
                          showLabels={false}
                          strokeColor={isPicked ? '#059669' : '#16A34A'}
                        />
                        <div className="mt-1 text-[10px] font-bold text-slate-700 truncate">{t.name}</div>
                        <div className="text-[9px] text-slate-500 font-mono-num">{Math.max(0, (t.folds || []).length - 1)}F · {g}mm</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {picked && (
        <button
          type="button"
          data-testid={`flashing-template-preview-${ref_id}`}
          onClick={() => setPreviewOpen(o => !o)}
          title={`Preview "${picked.name}"`}
          className="mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded border border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-100 text-[11px]"
        >🔍</button>
      )}
      {previewOpen && picked && (
        <div data-testid={`flashing-template-preview-popover-${ref_id}`} className="absolute z-30 top-full left-[180px] mt-1 bg-white border border-emerald-300 rounded-lg shadow-xl p-2 w-[340px]">
          <div className="flex items-center justify-between mb-1 px-0.5">
            <span className="text-[11px] font-bold text-slate-700">{picked.name}</span>
            <button type="button" onClick={() => setPreviewOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={12} /></button>
          </div>
          <FlashingProfileSVG
            folds={picked.folds || []}
            startAngle={Number(picked.start_angle_deg) || 0}
            width={320} height={170}
            showLabels={true}
            labelsLayout="perpendicular"
            strokeColor="#059669"
          />
          <div className="mt-1 text-[10px] text-slate-500 font-mono-num text-center">
            {(picked.folds || []).length} folds · {(picked.folds || []).reduce((a, f) => a + (Number(f.length_mm) || 0), 0)}mm girth · start {Number(picked.start_angle_deg) || 0}°
          </div>
          {picked.notes && <div className="mt-1 text-[10px] text-slate-600 italic px-1">{picked.notes}</div>}
        </div>
      )}
    </div>
  );
}
